import React, { useState, useEffect, useRef } from 'react';
import {
  DragDropContext,
  DropResult,
  DraggableLocation,
  DragStart
} from 'react-beautiful-dnd';
import { StateNodeConfig } from '@sierralabs/workflow';

import { ApiParams } from '../../common/api/api';
import * as Styled from './board/BoardPage.styled';
import BoardColumn from './board/BoardColumn';
import {
  mapTaskByStatus,
  createEmptyTaskMap,
  parseTaskStatus
} from './task.helper';
import { Task } from './Task.interface';
import { useGetTaskEntity, useFindTaskByEntity } from './Task.hooks';
import {
  WorkflowEntity,
  WorkflowVersion
} from '../workflow/Workflow.interface';
import { useFindWorkflowVersionByEntity } from '../workflow/Workflow.hooks';
import { TaskBoardStatusAction } from './TaskBoardStatusAction';
import { AppSnackbar } from '@sierralabs/react-material-ui';

const reorder = (task: Task[], startIndex: number, endIndex: number) => {
  const result = Array.from(task);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

const move = (
  source: Task[],
  destination: Task[],
  droppableSource: DraggableLocation,
  droppableDestination: DraggableLocation
) => {
  const sourceClone = Array.from(source);
  const destClone = Array.from(destination);
  const [removed] = sourceClone.splice(droppableSource.index, 1);

  destClone.splice(droppableDestination.index, 0, removed);

  const result = {} as any;
  result[droppableSource.droppableId] = sourceClone;
  result[droppableDestination.droppableId] = destClone;

  return result;
};

export interface TaskBoardProps {
  entity: WorkflowEntity;
  disabled?: boolean;
  apiParams?: ApiParams;
  tasks?: Task[];
  onSelectTask?: (task: Task) => void;
}

const TaskBoard: React.FC<TaskBoardProps> = (props: TaskBoardProps) => {
  const { entity, disabled, apiParams, tasks: tasksProp, onSelectTask } = props;
  const lastTaskMapRef = useRef<any>();
  const lastTransitionedTaskDataRef = useRef<Task | undefined>();
  const [workflowVersion, setWorkflowVersion] = useState<
    WorkflowVersion | undefined
  >();
  const [workflowStates, setWorkflowStates] = useState<any>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [taskMap, setTaskMap] = useState<any>();
  const [selectedTask, setSelectedTask] = useState<Task | undefined>();
  const [event, setEvent] = useState<any>(null);
  const {
    workflowVersion: loadedWorkflowVersion,
    loadingError,
    findWorkflowVersion
  } = useFindWorkflowVersionByEntity(entity);
  const { data: fetchedTasks, get: getTasks } = useFindTaskByEntity(entity);
  const [canTransition, setCanTransition] = useState<boolean | null>(null);
  const [onDrag, setOnDrag] = useState(false);
  const [selectedTaskEntity] = useGetTaskEntity(selectedTask?.id);

  useEffect(() => {
    findWorkflowVersion();
  }, [findWorkflowVersion]);

  useEffect(() => {
    !tasksProp && getTasks(apiParams);
  }, [apiParams, tasksProp, getTasks]);

  useEffect(() => {
    if (loadedWorkflowVersion) {
      setWorkflowVersion(loadedWorkflowVersion);
      setWorkflowStates(loadedWorkflowVersion.config.states);
    }
  }, [loadedWorkflowVersion]);

  useEffect(() => {
    if (tasksProp) {
      setTasks(tasksProp);
    } else if (fetchedTasks) {
      setTasks(fetchedTasks);
    }
  }, [tasksProp, fetchedTasks]);

  useEffect(() => {
    setTaskMap(mapTaskByStatus(workflowStates, tasks));
  }, [tasks, workflowStates]);

  useEffect(() => {
    if (canTransition === false) {
      revertTaskData();
      // will not revert task status since they're the same reference
      setTaskMap(lastTaskMapRef.current); // Revert transition.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canTransition]);

  const revertTaskData = () => {
    const previousTask: Task | undefined = lastTransitionedTaskDataRef.current;
    if (!previousTask) {
      return;
    }
    const taskToRevert: Task | undefined = tasks?.find(
      (task: Task) => task.id === previousTask.id
    );
    if (taskToRevert) {
      // revert data that was changed
      taskToRevert.status = previousTask.status;
    }
    // unset previously transitioned
    lastTransitionedTaskDataRef.current = undefined;
  };

  const handleTaskTransition = (
    lastSource: DraggableLocation,
    lastDestination: DraggableLocation
  ) => {
    if (!lastSource || !lastDestination) return;

    const source: StateNodeConfig =
      workflowStates[parseInt(lastSource.droppableId)];
    const destination: StateNodeConfig =
      workflowStates[parseInt(lastDestination.droppableId)];
    const eventFound = source.events?.find(
      event => destination.name === parseTaskStatus(event.transitions[0].target)
    );
    const task = taskMap[source.name][lastSource.index];
    if (!task) return;
    if (!eventFound) return;
    setEvent(eventFound);
    // save task data with no reference
    lastTransitionedTaskDataRef.current = { ...task };
    task.status = destination.name;
    const result = move(
      taskMap[source.name],
      taskMap[destination.name],
      lastSource,
      lastDestination
    );
    setTaskMap({
      ...taskMap,
      [source.name]: result[parseInt(lastSource.droppableId)],
      [destination.name]: result[parseInt(lastDestination.droppableId)]
    });
  };

  const handleDragEnd = (result: DropResult) => {
    setOnDrag(false);
    const { source, destination } = result;
    // dropped outside the list
    if (!destination) {
      return;
    }

    lastTaskMapRef.current = taskMap;

    if (source.droppableId === destination.droppableId) {
      const column: StateNodeConfig =
        workflowStates[parseInt(source.droppableId)];
      const items = reorder(
        taskMap[column.name],
        source.index,
        destination.index
      );
      setTaskMap({ ...taskMap, [column.name]: items });
    } else {
      handleTaskTransition(source, destination);
    }
  };

  const handleOnDragStart = (result: DragStart) => {
    const source: StateNodeConfig =
      workflowStates[parseInt(result.source.droppableId)];
    const task: Task = taskMap[source.name][result.source.index];
    // const { workflowId, name, entity, version } = workflowVersion!;
    // task.workflow = {
    //   id: workflowId,
    //   name,
    //   entity,
    //   version,
    //   status: WorkflowStatus.Published
    // };
    setEvent(null);
    setCanTransition(null);
    setSelectedTask(task);
    setOnDrag(true);
  };

  return (
    <>
      <DragDropContext
        onDragStart={result => {
          !disabled && handleOnDragStart(result);
        }}
        onDragEnd={result => {
          !disabled && handleDragEnd(result);
        }}
      >
        <Styled.Board>
          {workflowStates?.map((state: StateNodeConfig, index: number) => {
            const tasks = taskMap[state.name] || [];
            tasks[
              tasks.findIndex((task: any) => task?.id === selectedTask?.id)
            ] = selectedTask;
            return (
              <BoardColumn
                key={index}
                title={state.name}
                tasks={tasks}
                selectedTask={selectedTask}
                onDrag={onDrag}
                disabled={disabled}
                droppableId={index.toString()}
                onSelectTask={onSelectTask}
              />
            );
          })}
        </Styled.Board>
      </DragDropContext>
      {selectedTask && (
        <TaskBoardStatusAction
          selectedTask={selectedTask}
          taskEntity={selectedTaskEntity}
          event={event}
          doTransition={(value: boolean) => setCanTransition(value)}
          onChange={(task: Task) => {
            // setTaskMap() here sometimes moves nonselected tasks for some reason
            // const index = tasks?.findIndex(t => t.id === task.id) || -1;
            // console.log('changed task', task);
            // if (tasks && index > -1) {
            //   console.log('updating task index', index);
            //   tasks.splice(index, 1, task);
            //   setTaskMap(mapTaskByStatus(workflowStates, [...tasks]));
            // }
            setSelectedTask(task);
          }}
        />
      )}
      {loadingError && (
        <AppSnackbar open={Boolean(loadingError)} severity='error'>
          Error finding a published workflow for '{entity}'
        </AppSnackbar>
      )}
    </>
  );
};

export default TaskBoard;
